import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, STATUS_META } from "../lib/api";
import { ArrowLeft, Eye, Clock, CheckCircle2, Copy, Flame, ArrowDownToLine, Send, MousePointerClick, Gavel, Mail, Globe, Users, Trash2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const fmtDelay = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0)} h`;
  return `${(h / 24).toFixed(1)} j`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null);
// Durée lisible : "45 s", "12 min 30 s", "1 h 54 min".
const fmtDuration = (s) => {
  s = Math.round(s || 0);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60), sec = s % 60;
  if (m < 60) return sec ? `${m} min ${sec} s` : `${m} min`;
  const h = Math.floor(m / 60), min = m % 60;
  return min ? `${h} h ${min} min` : `${h} h`;
};

export default function ProposalAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get(`/proposals/${id}/analytics`).then((r) => setA(r.data))
      .catch(() => { toast.error("Introuvable"); navigate("/proposals"); });
  }, [id, navigate]);

  if (!a) return <div className="p-12 text-muted-foreground">Chargement…</div>;

  const meta = STATUS_META[a.status] || STATUS_META.brouillon;
  const sectionData = Object.entries(a.time_by_section).map(([name, value]) => ({ name, value }));
  const totalTime = sectionData.reduce((s, x) => s + x.value, 0);
  const heatmap = a.heatmap || [];
  const scroll = Math.round(a.max_scroll || 0);
  const eng = a.engagement || 0;
  const engColor = eng >= 70 ? "#10B981" : eng >= 40 ? "#F59E0B" : "#F43F5E";
  // SVG ring geometry
  const R = 52, C = 2 * Math.PI * R;

  const timeline = [
    { key: "sent", label: "Envoyée", icon: Send, date: a.sent_at },
    { key: "email_opened", label: "Email ouvert", icon: Mail, date: a.email_opened_at },
    { key: "viewed", label: "1ʳᵉ ouverture", icon: MousePointerClick, date: a.first_viewed_at },
    { key: "decided", label: a.status === "refusée" ? "Refusée" : "Acceptée", icon: Gavel, date: a.decided_at, done: !!a.decided_at },
  ];

  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/p/${a.public_id}`); toast.success("Lien copié"); };

  const clearTracking = async () => {
    if (!window.confirm("Effacer définitivement les données de suivi de cette proposition (vues, temps de lecture, adresses IP) ? Action irréversible, conforme au droit à l'effacement (RGPD).")) return;
    try {
      await api.delete(`/proposals/${id}/tracking`);
      toast.success("Données de suivi effacées");
      const r = await api.get(`/proposals/${id}/analytics`);
      setA(r.data);
    } catch { toast.error("Erreur lors de l'effacement"); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-5xl">
      <button onClick={() => navigate("/proposals")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="analytics-back">
        <ArrowLeft className="w-4 h-4" /> Retour aux propositions
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Suivi & analytics</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">{a.title}</h1>
          <span className={`inline-block mt-3 text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearTracking} data-testid="analytics-clear-tracking" className="flex items-center gap-2 border border-border px-4 h-10 rounded-sm text-sm font-medium text-muted-foreground hover:border-destructive hover:text-destructive transition-all">
            <Trash2 className="w-4 h-4" /> Effacer le suivi
          </button>
          <button onClick={copyLink} data-testid="analytics-copy-link" className="flex items-center gap-2 border border-border px-4 h-10 rounded-sm text-sm font-medium hover:border-primary hover:text-primary transition-all">
            <Copy className="w-4 h-4" /> Copier le lien
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border rounded-sm p-6" data-testid="analytics-views">
          <Eye className="w-5 h-5 text-primary mb-3" />
          <p className="font-heading text-3xl font-bold">{a.views}</p>
          <p className="text-sm text-muted-foreground">Vues</p>
          {a.opens != null && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-opens">
              dont <span className="font-medium text-foreground">{a.opens}</span> ouverture{a.opens > 1 ? "s" : ""} au total
            </p>
          )}
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <Clock className="w-5 h-5 text-primary mb-3" />
          <p className="font-heading text-3xl font-bold">{fmtDuration(totalTime)}</p>
          <p className="text-sm text-muted-foreground">Temps total lu</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <CheckCircle2 className="w-5 h-5 text-primary mb-3" />
          <p className="font-heading text-3xl font-bold">{fmtMoney(a.total, a.currency)}</p>
          <p className="text-sm text-muted-foreground">Montant</p>
        </div>
      </div>

      {/* Engagement + parcours */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border rounded-sm p-6 flex flex-col items-center justify-center" data-testid="analytics-engagement">
          <div className="flex items-center gap-1.5 self-start mb-2">
            <Flame className="w-4 h-4 text-primary" />
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Score d'engagement</span>
          </div>
          <div className="relative w-36 h-36 my-2">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r={R} fill="none" stroke="#E5E7EB" strokeWidth="10" />
              <circle cx="60" cy="60" r={R} fill="none" stroke={engColor} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={C} strokeDashoffset={C * (1 - eng / 100)} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-4xl font-bold tabular-nums">{eng}</span>
              <span className="text-xs text-muted-foreground">/ 100</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">{a.sections_seen}/{a.n_sections} sections consultées</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6" data-testid="analytics-scroll">
          <div className="flex items-center gap-1.5 mb-4">
            <ArrowDownToLine className="w-4 h-4 text-primary" />
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Profondeur de lecture</span>
          </div>
          <div className="flex items-end gap-2">
            <p className="font-heading text-4xl font-bold tabular-nums">{scroll}%</p>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${scroll}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-3">Part du document que le prospect a fait défiler au maximum.</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6" data-testid="analytics-timeline">
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Parcours du prospect</span>
          <div className="mt-4 space-y-4">
            {timeline.map((t, i) => (
              <div key={t.key} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${t.date ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                  <t.icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(t.date) || "En attente"}</p>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-border" data-testid="analytics-email-opened">
              <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
              <p className="text-xs text-muted-foreground">
                Email ouvert :{" "}
                {a.email_opened
                  ? <span className="font-semibold text-foreground">Oui{fmtDate(a.email_opened_at) ? ` · ${fmtDate(a.email_opened_at)}` : ""}{a.email_opens > 1 ? ` (${a.email_opens}×)` : ""}</span>
                  : <span className="font-semibold text-foreground">Non</span>}
              </p>
            </div>
            {a.decision_hours != null && (
              <p className="text-xs text-muted-foreground pt-1 border-t border-border">Décision en <span className="font-semibold text-foreground">{fmtDelay(a.decision_hours)}</span> après l'envoi.</p>
            )}
          </div>
        </div>
      </div>

      {heatmap.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-6 mb-8" data-testid="analytics-heatmap">
          <h3 className="font-heading text-lg font-semibold mb-1">Carte d'attention</h3>
          <p className="text-sm text-muted-foreground mb-5">Intensité de lecture par section — plus c'est foncé, plus le prospect s'y est attardé.</p>
          <div className="space-y-2">
            {heatmap.map((h) => (
              <div key={h.section} className="flex items-center gap-3">
                <span className="text-sm w-40 shrink-0 truncate">{h.section}</span>
                <div className="flex-1 h-7 rounded-sm bg-muted overflow-hidden relative">
                  <div className="h-full rounded-sm flex items-center justify-end pr-2" style={{ width: `${Math.max(h.intensity * 100, 6)}%`, backgroundColor: `rgba(0,71,255,${0.25 + h.intensity * 0.6})` }}>
                    <span className="text-[11px] font-mono font-semibold text-white whitespace-nowrap">{fmtDuration(h.seconds)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm p-6 mb-8">
        <h3 className="font-heading text-lg font-semibold mb-6">Temps passé par section</h3>
        {sectionData.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Aucune donnée d'engagement pour l'instant. Les statistiques apparaîtront dès que le client ouvrira la proposition.</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sectionData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} tickFormatter={fmtDuration} />
              <YAxis type="category" dataKey="name" width={140} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} />
              <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ borderRadius: 4, border: "1px solid #E5E7EB", fontSize: 13 }} formatter={(v) => [fmtDuration(v), "Temps"]} />
              <Bar dataKey="value" fill="#0047FF" radius={[0, 3, 3, 0]} barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Visiteurs (IP) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-sm p-6" data-testid="analytics-visitors">
          <div className="flex items-center gap-1.5 mb-4">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Visiteurs</h3>
            {(a.n_visitors ?? 0) > 0 && (
              <span className="text-xs text-muted-foreground ml-1">· {a.n_visitors} adresse{a.n_visitors > 1 ? "s" : ""} IP</span>
            )}
          </div>
          {(a.visitors || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Aucune consultation pour l'instant.</p>
          ) : (
            <div className="space-y-1">
              {a.visitors.map((v, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="font-mono text-sm truncate">{v.ip}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-medium">{v.opens} ouverture{v.opens > 1 ? "s" : ""}</p>
                    <p className="text-[11px] text-muted-foreground">{fmtDate(v.last)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Journal des ouvertures */}
        <div className="bg-card border border-border rounded-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Journal des ouvertures</h3>
          {(a.visits || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Aucune ouverture enregistrée.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {a.visits.map((e, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-border last:border-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <MousePointerClick className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="font-mono text-xs truncate">{e.ip}</span>
                  </span>
                  <span className="text-muted-foreground text-xs shrink-0">{fmtDate(e.ts)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
